import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { faList, faUserCog, faUser, faFileInvoiceDollar, faCalendar } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';

import axios from 'axios';

import AdminPageContainer from '../common/AdminPageContainer';

import Loader from '../../common/loader/Loader';
import Alert from '../../common/Alert';

import { BackLink, IconLink, IconButton } from '../common/Buttons';
import { checkUserAuth } from '../../../utils/auth-utils';
import ConfirmationModal, { useConfirmationModal } from '../../common/modals/ConfirmationModal';

const AdminUsersPage = props => {
  const { token, user: currentUser } = useSelector(state => state);

  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState({ isLoading: false, isError: false });

  const loadData = () => {
    setStatus({ isLoading: true, isError: false });
    axios
      .get('/api/v1/admin/users', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(r => {
        setStatus({ isLoading: false, isError: false });
        setUsers(r.data.users);
      })
      .catch(e => {
        setStatus({ isLoading: false, isError: true });
        checkUserAuth(e, props.history);
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  const changeIsActive = userId => value => {
    setStatus({ isLoading: true, isError: false });
    axios
      .put(
        `/api/v1/admin/users/${userId}/changeIsActive`,
        { isActive: value },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then(() => {
        loadData();
      })
      .catch(e => {
        setStatus({ isLoading: false, isError: true });
        checkUserAuth(e, props.history);
      });
  };

  const changeIsAdmin = userId => value => {
    setStatus({ isLoading: true, isError: false });
    axios
      .put(
        `/api/v1/admin/users/${userId}/changeIsAdmin`,
        { isActive: value },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then(() => {
        loadData();
      })
      .catch(e => {
        setStatus({ isLoading: false, isError: true });
        checkUserAuth(e, props.history);
      });
  };

  const confirmation = useConfirmationModal();

  const openDeactivateConfirmation = userId => () => {
    confirmation.open(() => () => {
      changeIsActive(userId)(false);
    });
  };

  const openActivateConfirmation = userId => () => {
    confirmation.open(() => () => {
      changeIsActive(userId)(true);
    });
  };

  const openSetAdminConfirmation = userId => () => {
    confirmation.open(() => () => {
      changeIsAdmin(userId)(true);
    });
  };

  const openUnsetAdminConrifmation = userId => () => {
    confirmation.open(() => () => {
      changeIsAdmin(userId)(false);
    });
  };

  return (
    <AdminPageContainer>
      <Loader isVisible={status.isLoading} />
      {status.isError && <Alert isError message="An unknown error occured" />}
      <ConfirmationModal state={confirmation} />

      <BackLink to="/admin" />

      <div className="mt-20px">
        {users.map(user => (
          <div key={user._id} className="admin-table-row">
            <span className="admin-table-row-title">{user.name}</span>
            <div className="is-flex is-flex-wrap">
              <IconLink
                to={`/admin/users/${user._id}/courses`}
                className="button is-link ml-10px mt-10px"
                text="Courses"
                icon={faList}
              />
              <IconLink
                to={`/admin/users/${user._id}/payments`}
                icon={faFileInvoiceDollar}
                text="Payments"
                className="button is-success ml-10px mt-10px"
              />
              <IconLink
                to={`/admin/users/${user._id}/timetable`}
                icon={faCalendar}
                text="Timetable"
                className="button is-info ml-10px mt-10px"
              />
              {!user.isOwner && user._id !== currentUser.id && (
                <>
                  {!user.isAdmin && (
                    <IconButton
                      onClick={openSetAdminConfirmation(user._id)}
                      className="button is-warning ml-10px mt-10px"
                      text="Set Admin"
                      icon={faUserCog}
                    />
                  )}
                  {user.isAdmin && (
                    <IconButton
                      onClick={openUnsetAdminConrifmation(user._id)}
                      className="button is-warning ml-10px mt-10px"
                      text="Unset Admin"
                      icon={faUserCog}
                    />
                  )}
                  {user.isActive && (
                    <IconButton
                      onClick={openDeactivateConfirmation(user._id)}
                      className="button is-danger ml-10px mt-10px"
                      text="Deactivate"
                      icon={faUser}
                    />
                  )}
                  {!user.isActive && (
                    <IconButton
                      onClick={openActivateConfirmation(user._id)}
                      className="button is-success ml-10px mt-10px"
                      text="Activate"
                      icon={faUserCog}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </AdminPageContainer>
  );
};

AdminUsersPage.propTypes = {
  history: PropTypes.object.isRequired,
};

export default AdminUsersPage;
